import React from "react";
import Stack from "./Stack";
import PropTypes from "prop-types";

/**
 * HStack (Horizontal Stack) - A Stack component that arranges children horizontally on larger screens,
 * and automatically becomes vertical (column) on small screens for better mobile responsiveness.
 * 
 * Default responsive behavior:
 * - Small screens (xs, sm): column layout
 * - Medium screens and up (md, lg, xl): row layout
 * 
 * Spacing is automatically adjusted: smaller on mobile (column), original on desktop (row)
 * to prevent huge vertical gaps when switching to column layout.
 * 
 * This is a convenience wrapper that implements mobile-first responsive design pattern.
 * 
 * @component
 * @example
 * // Buttons side-by-side on desktop, stacked on mobile
 * <HStack spacing={2}>
 *   <Button>Cancel</Button>
 *   <Button>Submit</Button>
 * </HStack>
 * 
 * @example
 * // Form fields in a row on desktop, stacked on mobile
 * <HStack spacing={2} alignItems="flex-start">
 *   <TextField label="First Name" />
 *   <TextField label="Last Name" />
 * </HStack>
 * 
 * @example
 * // Custom breakpoint for responsive behavior
 * <HStack spacing={2} direction={{ xs: 'column', lg: 'row' }}>
 *   <Box>Item 1</Box>
 *   <Box>Item 2</Box>
 * </HStack>
 */
const HStack = React.forwardRef(({ children, direction, spacing, ...props }, ref) => {
  // Default responsive direction: column on small screens, row on medium+
  // Allow override if direction prop is explicitly provided
  const responsiveDirection = direction || { xs: "column", md: "row" };

  // Make spacing responsive: smaller on mobile (column), original on desktop (row)
  // This prevents huge vertical gaps when switching to column layout
  // Only apply if spacing is a number and direction is responsive
  // If spacing is already an object (responsive), use it as-is
  const isResponsiveDirection = typeof responsiveDirection === 'object';
  const responsiveSpacing = isResponsiveDirection && 
                            spacing && 
                            typeof spacing === 'number'
    ? {
        xs: 2, // Small spacing on mobile (column mode) - prevents huge gaps
        md: spacing, // Original spacing on desktop (row mode)
      }
    : spacing;

  return (
    <Stack ref={ref} direction={responsiveDirection} spacing={responsiveSpacing} {...props}>
      {children}
    </Stack>
  );
});

HStack.displayName = 'HStack';

HStack.propTypes = {
  /**
   * The content of the component
   */
  children: PropTypes.node,
  /**
   * Defines the flex-direction style property
   * Default is responsive: { xs: 'column', md: 'row' }
   * Can be overridden with a string or custom responsive object
   */
  direction: PropTypes.oneOfType([
    PropTypes.oneOf(["row", "row-reverse", "column", "column-reverse"]),
    PropTypes.shape({
      xs: PropTypes.oneOf(["row", "row-reverse", "column", "column-reverse"]),
      sm: PropTypes.oneOf(["row", "row-reverse", "column", "column-reverse"]),
      md: PropTypes.oneOf(["row", "row-reverse", "column", "column-reverse"]),
      lg: PropTypes.oneOf(["row", "row-reverse", "column", "column-reverse"]),
      xl: PropTypes.oneOf(["row", "row-reverse", "column", "column-reverse"]),
    }),
  ]),
  /**
   * Defines the space between immediate children
   * Can be a number (theme spacing multiplier) or object for responsive values
   * When using responsive direction, numeric spacing is automatically adjusted:
   * - Mobile (xs): spacing={2} for reasonable vertical gaps
   * - Desktop (md+): original spacing value for horizontal layout
   */
  spacing: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.shape({
      xs: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      sm: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      md: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      lg: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      xl: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ]),
  /**
   * Defines the align-items style property
   */
  alignItems: PropTypes.oneOfType([
    PropTypes.oneOf([
      "flex-start",
      "center",
      "flex-end",
      "stretch",
      "baseline",
    ]),
    PropTypes.object,
  ]),
  /**
   * Defines the justify-content style property
   */
  justifyContent: PropTypes.oneOfType([
    PropTypes.oneOf([
      "flex-start",
      "center",
      "flex-end",
      "space-between",
      "space-around",
      "space-evenly",
    ]),
    PropTypes.object,
  ]),
  /**
   * If true, the CSS flexbox gap is used instead of applying margin to children
   */
  useFlexGap: PropTypes.bool,
  /**
   * The component used for the root node
   */
  component: PropTypes.elementType,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])
    ),
    PropTypes.func,
    PropTypes.object,
  ]),
};

// No defaultProps - inherit from underlying Stack/MUI behavior

export default HStack;
